
// Fix: Use named imports from reactflow for Node and Edge types.
import { Node, Edge } from 'reactflow';

export type { Edge };

export enum NodeCategory {
  Start = 'Start Events',
  End = 'End Events',
  Task = 'Tasks',
  Gateway = 'Gateways',
  Activity = 'Activities',
  Intermediate = 'Intermediate Events',
  Boundary = 'Boundary Events'
}

/**
 * Generic AI Service interface for workflow automation and decision making.
 */
export interface AIService {
  /**
   * Generates text content based on a prompt, typically for automated task completion.
   */
  generateWorkflowCompletion(prompt: string, model?: string): Promise<string>;
  
  /**
   * Evaluates a logical expression or condition within a specific data context.
   */
  evaluateCondition(context: any, condition: string): Promise<boolean>;
}

export interface NodeTypeConfig {
  type: string;
  label: string;
  color: string;
  icon: string; // Lucide icon name
  category: NodeCategory;
  description: string;
  disabled?: boolean;
}

export interface ExtractionRule {
  method: 'jsonpath' | 'xpath' | 'regex';
  source: string; // The path or pattern
  targetVar: string; // Variable name
  type?: 'string' | 'number' | 'boolean' | 'object' | 'array'; // Casting type (Optional)
}

export interface WorkflowNodeData {
  label: string;
  description?: string;
  config?: Record<string, any>;
  status?: 'idle' | 'active' | 'completed' | 'error' | 'waiting';
  outputs?: string[];
  conditions?: Array<{ path: number; expression: string; priority?: number; label?: string; description?: string }>;
  hasBreakpoint?: boolean;
  cancelActivity?: boolean;
  assignments?: string[];
  isConnectableTarget?: boolean;
  
  // HierarchyFlow Stats
  directCount?: number;
  inclusiveCount?: number;

  // Start Event Specific
  messageName?: string;
  messageVariableName?: string;
  messagePayloadVariableName?: string;
  messagePayload?: any;
  signalName?: string;
  timerDefinition?: string;
  timerType?: string;
  timerDate?: string;
  endDate?: string;
  timezone?: string;
  
  // Task Specific
  script?: string;
  scriptLanguage?: string;
  
  // Service Specific
  resultVariable?: string;
  
  // Response Extraction
  exampleResponse?: string;
  extractionRules?: ExtractionRule[];

  // Form Specific
  formSchema?: any;
  formTitle?: string;

  // Extensions
  userTaskType?: string;
  extensionConfig?: any;
  extensionRelations?: any;
  
  // Produce Task Specific
  productionProcessKey?: string;
  productionOperations?: Array<{ key: string; value: string }>;

  // SmartBridge Specific
  jobScript?: any;

  // Email Service
  fromEmail?: string;
  fromName?: string;
  to?: string;
  toNames?: string;
  cc?: string;
  ccNames?: string;
  bcc?: string;
  bccNames?: string;
  subject?: string;
  body?: string;
  isHtml?: boolean;
  attachments?: string[];

  // SMS Service
  toPhone?: string;
  message?: string;
  
  // REST Service
  restTopic?: string;
  service?: string;
  endpoint?: string;
  method?: string;
  retries?: number;
  onException?: number;
  actorType?: string;
  headers?: any;
  requestBody?: string;
  authentication?: string;
  bearerToken?: string;
  apiKey?: string;

  // Database Service
  connectionId?: string;
  queryType?: string;
  query?: string;
  timeout?: number;
  parameters?: Array<{ key: string; value: string; type: string }>;

  // MCP Service
  mcpServerUrl?: string;
  mcpServerName?: string;
  mcpOperation?: string;
  mcpToolName?: string;
  mcpToolArguments?: string;
  mcpResourceUri?: string;
  mcpPromptName?: string;
  mcpPromptArguments?: string;

  // LLM Service
  model?: string;
  systemPrompt?: string;
  userPrompt?: string;
  outputVariable?: string;

  // Call Activity Specific
  processName?: string;
  processCollection?: string;
  processVersion?: number;
  keepBusinessKey?: boolean;
  sequentialLoop?: boolean;
  asAsyncCall?: boolean;
  calledElement?: string; // Legacy/Fallback
  inputVariables?: any;
  outputVariables?: any;

  // Error Handling (ErrorEndEvent)
  errorMessage?: string;
  errorPayload?: any;

  // Editor specific
  [key: string]: any;
}

export type AppNode = Node<WorkflowNodeData>;

export interface SimulationHistoryFrame {
  nodeId: string;
  variables: Record<string, any>;
  timestamp: number;
}

export interface SimulationState {
  isRunning: boolean;
  currentNodeId: string | null;
  logs: string[];
  variables: Record<string, any>;
  history: SimulationHistoryFrame[];
  isWaitingForInput: boolean;
  executionQueue: string[];
  visitedEdgeIds: string[];
}

export interface ProcessDefinition {
  id: string;
  name: string;
  description?: string;
  isSubProcess?: boolean;
  nodes: AppNode[];
  edges: Edge[];
}

export interface EngineResource {
  Kind: number;
  Data?: string;
  IsBase64Data?: boolean;
  Source?: string;
  ReturnVariable?: string;
}

export interface EngineMessageObject {
  StaticText?: string;
  VariableName?: string;
  PayloadVariableName?: string;
  Payload?: any;
}

export interface EngineProcessNameObject {
  Collection: string;
  Version: number;
  Name: string;
}

export interface EngineNode {
  type: string;
  Id: number;
  X: number;
  Y: number;
  Key?: string;
  Text?: string;
  LabelPosition?: number;
  Extensions?: any[];
  comment?: string;
  
  Message?: string | EngineMessageObject;
  Signal?: string;
  Timer?: string;
  
  ExportAll?: boolean;
  ExportScripts?: number[];
  Error?: any;
  Reason?: string;
  
  Expressions?: Record<string, string>;
  ExpressionMapping?: Record<string, string>;
  IsMerging?: boolean;
  DefaultPath?: number;
  
  Script?: string;
  InputScripts?: number[];
  OutputScripts?: number[];
  BoundaryEvents?: number[];
  
  ImportAllToActivityContext?: boolean;
  ExportAllToTokenContext?: boolean;
  
  ResultVariable?: string;
  ErrorBehaviour?: number;
  
  ProcessName?: EngineProcessNameObject;
  KeepBusinessKey?: boolean;
  AsAsyncCall?: boolean;
  SequentialLoop?: boolean;
  CollectionName?: string;
  ElementName?: string;
  
  Activity?: number;
  ExceptionType?: string;
  ErrorMessage?: string;
  Kind?: number;
}

export interface EngineConnection {
  From: number;
  To: number;
  Points?: string[];
  IsDefault?: boolean;
  ExpressionName?: string;
  StartPoint?: number;
  EndPoint?: number;
}

export interface EngineRecursiveConnection {
  From: number;
  To: number;
}

export interface EngineProcess {
  Name: string;
  IsSubProcess: boolean;
  Nodes: EngineNode[];
  Connections: EngineConnection[];
  RecursiveConnections: EngineRecursiveConnection[];
}

export interface WorkflowProject {
  Name: string;
  Version: number;
  Resources: Record<string, EngineResource>;
  Processes: EngineProcess[];
}
